/* Capsi theme — global interactivity.
 * Dependency-free vanilla JS re-implementing what the React app did:
 *   - SiteNav: sticky-scroll class + mobile burger menu
 *   - Cart drawer: open/close + AJAX add-to-cart (subscription selling plans),
 *     live line items, remove, subtotal + nav count
 *   - FAQ accordion (one open at a time)
 * Handlers are defensive: missing elements simply no-op.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Money formatting (canonical Shopify formatMoney)
   * ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' || opt === null ? def : opt;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value = '';
    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ''); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }
  window.theme = window.theme || {};
  window.theme.formatMoney = formatMoney;

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ------------------------------------------------------------------ *
   * Sticky nav
   * ------------------------------------------------------------------ */
  function initStickyNav() {
    var nav = document.querySelector('[data-site-nav], .nav');
    if (!nav) return;
    var threshold = 12;
    var onScroll = function () { nav.classList.toggle('scrolled', window.scrollY > threshold); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------ *
   * Mobile burger menu
   * ------------------------------------------------------------------ */
  function initBurger() {
    var burger = document.querySelector('[data-nav-burger]');
    var menu = document.querySelector('[data-nav-mobile]');
    if (!burger || !menu) return;

    function openMenu() {
      menu.classList.add('open');
      burger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('scroll-locked');
      // Move focus into the menu so keyboard users land somewhere sensible.
      var firstLink = menu.querySelector('a');
      if (firstLink) firstLink.focus();
    }
    function closeMenu() {
      menu.classList.remove('open');
      burger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('scroll-locked');
    }

    burger.addEventListener('click', function () {
      if (menu.classList.contains('open')) closeMenu(); else openMenu();
    });
    menu.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', closeMenu);
    });
    // Follows the same Escape-to-close pattern as the cart drawer (below).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('open')) {
        closeMenu();
        burger.focus();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart drawer + AJAX cart
   * ------------------------------------------------------------------ */
  function formatCount(n) {
    if (!n) return '0';
    return n > 99 ? '99+' : ('00' + n).slice(-2);
  }

  function renderDrawer(cart) {
    var body = document.querySelector('[data-cart-body]');
    if (body) {
      if (!cart.item_count) {
        body.innerHTML =
          '<div class="drawer-empty">' +
            '<div class="serif" style="font-size:28px;margin-bottom:8px;">Empty for now.</div>' +
            '<div class="mono">Take the 3-minute assessment to build your pack.</div>' +
            '<a class="btn btn-primary" style="margin-top:16px;" href="/pages/quiz">Start the assessment &rarr;</a>' +
          '</div>';
      } else {
        body.innerHTML = cart.items.map(function (item) {
          var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
            ? ' &middot; ' + escapeHtml(item.selling_plan_allocation.selling_plan.name) : '';
          var img = item.image
            ? '<img src="' + item.image.replace(/(\.[^.]+)(\?.*)?$/, '_120x$1') + '" alt="" width="60" height="60">'
            : '<div class="puck"></div>';
          return '' +
            '<div class="cart-item" data-cart-item>' +
              '<div class="pic-mini">' + img + '</div>' +
              '<div>' +
                '<div class="name">' + escapeHtml(item.product_title || item.title) + '</div>' +
                '<div class="qty">Qty ' + item.quantity + plan + ' &middot; ' +
                  '<button type="button" data-cart-remove="' + escapeHtml(item.key) + '" style="text-decoration:underline;">Remove</button>' +
                '</div>' +
              '</div>' +
              '<div class="px">' + formatMoney(item.final_line_price) + '</div>' +
            '</div>';
        }).join('');
      }
    }
    var subtotal = document.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = '[' + formatCount(cart.item_count) + ']';
    });

    var checkoutBtn = document.querySelector('[data-cart-drawer] [name="checkout"]');
    if (checkoutBtn) checkoutBtn.disabled = cart.item_count === 0;
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderDrawer(cart); return cart; });
  }

  /* Prefill the checkout contact email from the address the visitor gave in the
   * quiz. The quiz stores its result (incl. email) in sessionStorage for the
   * current tab and localStorage across visits; Shopify reads the hidden
   * checkout[email] field on the cart->checkout POST and prefills contact info. */
  function storedQuizEmail() {
    try {
      var raw = sessionStorage.getItem('capsi_quiz_result') ||
                localStorage.getItem('capsi_quiz_result_v1');
      if (!raw) return '';
      var r = JSON.parse(raw);
      return (r && typeof r.email === 'string') ? r.email : '';
    } catch (e) { return ''; }
  }
  function prefillCheckoutEmail() {
    var email = storedQuizEmail();
    if (!email) return;
    document.querySelectorAll('[data-checkout-email]').forEach(function (input) {
      input.value = email;
    });
  }

  function initCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var mask = document.querySelector('[data-cart-mask]');
    /* On immersive pages (quiz / results) the drawer + mask are display:none and
       the page owns the cart button (it scrolls to the on-page summary). Opening
       the global drawer here would add body.scroll-locked with no visible mask or
       close button left to remove it — trapping the page. So open() no-ops there. */
    var immersive = document.body.classList.contains('template-immersive');
    function open() { if (immersive || !drawer) return; drawer.classList.add('open'); if (mask) mask.classList.add('open'); document.body.classList.add('scroll-locked'); }
    function close() { if (drawer) drawer.classList.remove('open'); if (mask) mask.classList.remove('open'); document.body.classList.remove('scroll-locked'); }

    document.querySelectorAll('[data-cart-toggle]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    document.querySelectorAll('[data-cart-close]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });
    if (mask) mask.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    // Remove line items (event delegation on the drawer body).
    var body = document.querySelector('[data-cart-body]');
    if (body) {
      body.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-cart-remove]') : null;
        if (!btn) return;
        e.preventDefault();
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: btn.getAttribute('data-cart-remove'), quantity: 0 })
        }).then(function (r) { return r.json(); }).then(renderDrawer).catch(function () {});
      });
    }

    prefillCheckoutEmail();

    window.CapsiCart = { openDrawer: open, closeDrawer: close, refresh: refreshCart };
  }

  /* AJAX add-to-cart on every product form (cards + PDP). Honours the hidden
   * selling_plan input so subscription packs add as subscriptions. Falls back
   * to a native submit if the request fails. */
  function initAddToCart() {
    document.querySelectorAll('form[id^="product-form"]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var fd = new FormData(form);
        var item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) };
        var sp = fd.get('selling_plan');
        if (sp) item.selling_plan = parseInt(sp, 10);

        var btn = form.querySelector('button[type="submit"], [data-add-btn]');
        var original = btn ? btn.textContent : '';
        if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [item] })
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function () { return refreshCart(); })
          .then(function () {
            if (window.CapsiCart) window.CapsiCart.openDrawer();
            if (btn) { btn.textContent = original; btn.disabled = false; }
          })
          .catch(function () { form.submit(); });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * FAQ accordion (one open at a time)
   * ------------------------------------------------------------------ */
  function initAccordion() {
    var items = document.querySelectorAll('[data-faq-item]');
    if (!items.length) return;
    function syncAria() {
      items.forEach(function (i) {
        var q = i.querySelector('[data-faq-q]');
        if (q && q.hasAttribute('aria-expanded')) {
          q.setAttribute('aria-expanded', i.classList.contains('open') ? 'true' : 'false');
        }
      });
    }
    items.forEach(function (item) {
      var head = item.querySelector('[data-faq-q]') || item;
      head.addEventListener('click', function () {
        var wasOpen = item.classList.contains('open');
        items.forEach(function (i) { i.classList.remove('open'); });
        if (!wasOpen) item.classList.add('open');
        syncAria();
      });
    });
    syncAria();
  }

  /* ------------------------------------------------------------------ *
   * Pack card "See more" — reveals the rest of the description in place
   * ------------------------------------------------------------------ */
  function initSeeMore() {
    var btns = document.querySelectorAll('[data-see-more]');
    if (!btns.length) return;
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-product-card]') || btn.parentNode;
        var rest = card.querySelector('.pdesc-rest');
        if (!rest) return;
        var open = rest.hasAttribute('hidden') === false;
        if (open) {
          rest.setAttribute('hidden', '');
          btn.textContent = 'See more';
          btn.setAttribute('aria-expanded', 'false');
        } else {
          rest.removeAttribute('hidden');
          btn.textContent = 'See less';
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  function init() {
    initStickyNav();
    initBurger();
    initCartDrawer();
    initAddToCart();
    initAccordion();
    initSeeMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
